import { useQuery } from '@tanstack/react-query'
import { Download, FileText, MoreHorizontal, Pencil, Phone, PhoneMissed, Trash2, Video } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Avatar } from '@/components/ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { cn } from '@/utils/cn'
import { formatBytes } from '@/utils/format'
import { fileLink } from '../api'
import { duration, timeOf } from '../format'
import type { CallInfo, ChatMessage, MessageFile } from '../types'

interface MessageItemProps {
  message: ChatMessage
  /** Show the sender's name and picture (groups and class rooms, first of a run). */
  showSender: boolean
  /** Consecutive messages of one sender are grouped tightly. */
  continued: boolean
  onEdit: (message: ChatMessage) => void
  onDelete: (message: ChatMessage) => void
  onJoinCall: (message: ChatMessage) => void
}

const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g

/** Text with clickable links (kept as plain text otherwise: no HTML from users is ever rendered). */
function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE)
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all underline underline-offset-2"
          >
            {part}
          </a>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  )
}

export function MessageItem({
  message,
  showSender,
  continued,
  onEdit,
  onDelete,
  onJoinCall,
}: MessageItemProps) {
  const { t } = useTranslation('messaging')
  if (message.kind === 'call' && message.call) {
    return <CallLine message={message} call={message.call} onJoin={() => onJoinCall(message)} />
  }
  const mine = message.mine
  const name = message.sender?.name ?? t('Former member')

  return (
    <div
      className={cn(
        'group flex items-end gap-2',
        mine ? 'flex-row-reverse' : '',
        continued ? 'mt-0.5' : 'mt-3',
      )}
    >
      {!mine ? (
        showSender && !continued ? (
          <Avatar name={name} src={message.sender?.avatarUrl} size="sm" />
        ) : (
          <span className="w-8 shrink-0" aria-hidden="true" />
        )
      ) : null}
      <div className={cn('flex max-w-[min(34rem,85%)] flex-col', mine ? 'items-end' : 'items-start')}>
        {showSender && !continued && !mine ? (
          <p className="mb-1 ml-1 text-2xs font-medium text-muted">{name}</p>
        ) : null}
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm shadow-xs',
            message.deleted
              ? 'border border-dashed border-line bg-transparent text-muted italic'
              : mine
                ? 'rounded-br-md bg-brand-600 text-white'
                : 'rounded-bl-md border border-line bg-surface text-ink',
          )}
        >
          {message.deleted ? (
            t('Message deleted')
          ) : (
            <>
              {message.body ? (
                <p className="break-words whitespace-pre-wrap">{<Linkified text={message.body} />}</p>
              ) : null}
              {message.files.length > 0 ? (
                <div className={cn('flex flex-col gap-2', message.body ? 'mt-2' : '')}>
                  {message.files.map((f) => (
                    <Attachment key={f.id} file={f} mine={mine} />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
        <p className="mx-1 mt-0.5 text-2xs text-subtle">
          <time dateTime={message.createdAt}>{timeOf(message.createdAt)}</time>
          {message.editedAt && !message.deleted ? ` · ${t('edited')}` : ''}
        </p>
      </div>
      {!message.deleted && (message.canEdit || message.canDelete) ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="mb-5 grid size-7 place-items-center rounded-full text-subtle opacity-0 group-hover:opacity-100 hover:bg-surface-muted focus-visible:opacity-100 data-[state=open]:opacity-100"
              aria-label={t('Message actions')}
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={mine ? 'end' : 'start'}>
            {message.canEdit && message.body !== null ? (
              <DropdownMenuItem icon={<Pencil />} onSelect={() => onEdit(message)}>
                {t('Edit')}
              </DropdownMenuItem>
            ) : null}
            {message.canDelete ? (
              <DropdownMenuItem icon={<Trash2 />} tone="danger" onSelect={() => onDelete(message)}>
                {t('Delete')}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )
}

function CallLine({ message, call, onJoin }: { message: ChatMessage; call: CallInfo; onJoin: () => void }) {
  const { t } = useTranslation('messaging')
  const Icon = call.ongoing
    ? call.video
      ? Video
      : Phone
    : call.joinCount === 0
      ? PhoneMissed
      : call.video
        ? Video
        : Phone
  const who = message.mine ? t('You') : (call.startedBy?.name ?? t('Former member'))
  const label = call.ongoing
    ? call.video
      ? t('{{name}} started a video call', { name: who })
      : t('{{name}} started an audio call', { name: who })
    : call.joinCount === 0
      ? t('Missed call')
      : call.video
        ? t('Video call · {{duration}}', { duration: duration(call.duration ?? 0) })
        : t('Audio call · {{duration}}', { duration: duration(call.duration ?? 0) })

  return (
    <div className="my-4 flex justify-center">
      <div
        className={cn(
          'inline-flex items-center gap-3 rounded-full border px-4 py-2 text-xs',
          call.ongoing
            ? 'border-success-200 text-success-800 bg-success-50'
            : 'border-line bg-surface text-muted',
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
        <span>{label}</span>
        <span className="text-subtle">{timeOf(message.createdAt)}</span>
        {call.ongoing ? (
          <button
            type="button"
            onClick={onJoin}
            className="rounded-full bg-success-600 px-3 py-1 font-semibold text-white hover:bg-success-700"
          >
            {t('Join')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** Images, audio and videos play inline (short-lived signed links); other files download. */
function Attachment({ file, mine }: { file: MessageFile; mine: boolean }) {
  const { t } = useTranslation('messaging')
  const [busy, setBusy] = useState(false)
  const media = /^(image|audio|video)\//.test(file.mimeType)
  const inline = useQuery({
    queryKey: ['messaging', 'file', file.id],
    queryFn: () => fileLink(file.id, true),
    enabled: media,
    staleTime: 8 * 60_000,
  })

  const download = async () => {
    setBusy(true)
    try {
      const { url } = await fileLink(file.id)
      window.location.assign(url)
    } catch (e) {
      toast.error(t('Could not download the file'), toApiError(e).message)
    } finally {
      setBusy(false)
    }
  }

  if (media && inline.data) {
    if (file.mimeType.startsWith('image/')) {
      return (
        <a href={inline.data.url} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={inline.data.url}
            alt={file.name}
            className="max-h-72 rounded-lg object-contain"
            loading="lazy"
          />
        </a>
      )
    }
    if (file.mimeType.startsWith('audio/')) {
      // Voice notes and clips people send have no caption track.
      // eslint-disable-next-line jsx-a11y/media-has-caption
      return <audio controls src={inline.data.url} className="w-64 max-w-full" aria-label={file.name} />
    }
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video controls src={inline.data.url} className="max-h-72 rounded-lg" aria-label={file.name} />
  }

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={busy}
      className={cn(
        'flex w-64 max-w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
        mine ? 'bg-white/15 hover:bg-white/25' : 'bg-surface-muted hover:bg-surface-sunken',
      )}
    >
      <FileText className="size-5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{file.name}</span>
        <span className={cn('block text-2xs', mine ? 'text-white/70' : 'text-muted')}>
          {formatBytes(file.size)}
        </span>
      </span>
      <Download className="size-4 shrink-0" aria-hidden="true" />
    </button>
  )
}
